import api from '../api/axiosInstance';

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  newCount?: number;
}

export type NodeStatus = 'deployed' | 'failed' | 'new' | 'provisioned' | 'upgrading';
export type ControlPlane = 'none' | 'eiap' | 'enm' | 'rlo';
export type NodeSource = 'manual' | 'ciq' | 'enm';
export interface GnbNode {
  id: string;
  nodeName: string;
  nodeIp?: string;
  deploymentId?: string;
  currentVersion?: number;
  currentRelease?: string;
  currentReleaseVersion?: string;
  referenceId?: string;
  currentDraftName?: string;
  status: NodeStatus;
  controlPlane: ControlPlane;
  controlPlaneEndpoint?: string;
  hasStagedCiq: boolean;
  hasStagedDay1: boolean;
  enmRef?: string;
  environment?: string;
  source: NodeSource;
  lastSyncedAt?: string;
  createdAt: string;
  updatedAt: string;
  drafts?: GnbNodeDraft[];
}

export interface GnbNodeDraft {
  id: string;
  draftName: string;
  version?: number;
  status?: string;
  blueprintRef?: string;
  createdAt: string;
}

export interface CiqUploadResponse {
  results: { id: string }[];
  errors: { filename: string; error: string }[];
}

export const gnbNodesService = {
  getAll: (filters?: { endpoints?: string[]; enmRefs?: string[]; page?: number; limit?: number; nodeName?: string; nodeIp?: string; environment?: string; controlPlane?: string; controlPlaneEndpoint?: string; source?: string; referenceId?: string; sortBy?: string; sortOrder?: string }) => {
    const params = new URLSearchParams();
    filters?.endpoints?.forEach((e) => params.append('endpoint', e));
    filters?.enmRefs?.forEach((e) => params.append('enmRef', e));
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.nodeName) params.set('nodeName', filters.nodeName);
    if (filters?.nodeIp) params.set('nodeIp', filters.nodeIp);
    if (filters?.environment) params.set('environment', filters.environment);
    if (filters?.controlPlane) params.set('controlPlane', filters.controlPlane);
    if (filters?.controlPlaneEndpoint) params.set('controlPlaneEndpoint', filters.controlPlaneEndpoint);
    if (filters?.source) params.set('source', filters.source);
    if (filters?.referenceId) params.set('referenceId', filters.referenceId);
    if (filters?.sortBy) params.set('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.set('sortOrder', filters.sortOrder);
    const qs = params.toString();
    return api.get<PaginatedResponse<GnbNode>>(`/api/gnb-nodes${qs ? `?${qs}` : ''}`);
  },

  getById: (id: string) => api.get<GnbNode>(`/api/gnb-nodes/${id}`),

  getAllIds: (enmRefs?: string[]) => {
    const params = new URLSearchParams();
    enmRefs?.forEach((e) => params.append('enmRef', e));
    const qs = params.toString();
    return api.get<{ ids: string[] }>(`/api/gnb-nodes/ids${qs ? `?${qs}` : ''}`);
  },

  getDrafts: (id: string) => api.get<GnbNodeDraft[]>(`/api/gnb-nodes/${id}/drafts`),

  downloadCiq: (id: string) =>
    api.get(`/api/gnb-nodes/${id}/ciq`, { responseType: 'blob' }),

  downloadCiqArtifacts: (deploymentId: string) =>
    api.get(`/api/gnb-nodes/${deploymentId}/ciq/artifacts`, { responseType: 'blob' }),

  downloadStagedCiq: (id: string) =>
    api.get(`/api/gnb-nodes/${id}/ciq/staged`, { responseType: 'blob' }),

  downloadStagedDay1: (id: string) =>
    api.get(`/api/gnb-nodes/${id}/day1/staged`, { responseType: 'blob' }),

  previewCiq: (id: string) =>
    api.get<string>(`/api/gnb-nodes/${id}/ciq`, { responseType: 'text', transformResponse: [(data) => data] }),

  previewStagedCiq: (id: string) =>
    api.get<string>(`/api/gnb-nodes/${id}/ciq/staged`, { responseType: 'text', transformResponse: [(data) => data] }),

  previewStagedDay1: (id: string) =>
    api.get<string>(`/api/gnb-nodes/${id}/day1/staged`, { responseType: 'text', transformResponse: [(data) => data] }),

  uploadCiq: (id: string, content: string) =>
    api.post(`/api/gnb-nodes/${id}/ciq/staged`, { content }),

  uploadStagedDay1: (id: string, content: string) =>
    api.post(`/api/gnb-nodes/${id}/day1/staged`, { content }),

  deleteStagedDay1: (id: string) =>
    api.delete(`/api/gnb-nodes/${id}/day1/staged`),

  uploadCiqGeneral: (files: { name: string; content: string }[], workflowTicketId?: string) =>
    api.post<CiqUploadResponse>('/api/gnb-nodes/upload-ciq', { files, workflowTicketId }),

  deleteStagedCiq: (id: string) =>
    api.delete(`/api/gnb-nodes/${id}/ciq/staged`),

  // ── Ticket-scoped staging (per node PER TICKET) ──
  tsStageCiq: (id: string, ctx: { workflowTicketId: string; ticketId: string; automationFlowId: string }, content: string) =>
    api.post(`/api/gnb-nodes/${id}/ticket-staging/ciq`, { ...ctx, content }),

  tsDeleteCiq: (id: string, ctx: { workflowTicketId: string; ticketId: string; automationFlowId: string }) =>
    api.delete(`/api/gnb-nodes/${id}/ticket-staging/ciq`, { params: ctx }),

  tsPreviewCiq: (id: string, ticketId: string) =>
    api.get<string>(`/api/gnb-nodes/${id}/ticket-staging/ciq/preview`, {
      params: { ticketId },
      responseType: 'text',
      transformResponse: [(data) => data]
    }),

  tsListDay1: (id: string, ticketId: string) =>
    api.get<{ files: string[] }>(`/api/gnb-nodes/${id}/ticket-staging/day1`, { params: { ticketId } }),

  tsStageDay1File: (
    id: string,
    ctx: { workflowTicketId: string; ticketId: string; automationFlowId: string },
    fileName: string,
    content: string
  ) => api.post(`/api/gnb-nodes/${id}/ticket-staging/day1`, { ...ctx, fileName, content }),

  tsDeleteDay1File: (
    id: string,
    ctx: { workflowTicketId: string; ticketId: string; automationFlowId: string },
    fileName: string
  ) => api.delete(`/api/gnb-nodes/${id}/ticket-staging/day1/${encodeURIComponent(fileName)}`, { params: ctx }),

  tsPreviewDay1File: (id: string, ticketId: string, fileName: string) =>
    api.get<string>(`/api/gnb-nodes/${id}/ticket-staging/day1/${encodeURIComponent(fileName)}/preview`, {
      params: { ticketId },
      responseType: 'text',
      transformResponse: [(data) => data]
    }),

  sync: (sourceUrl: string, automationFlowId: string) =>
    api.post('/api/gnb-nodes/sync', { automationFlowId }, { headers: { 'x-source-url': sourceUrl } }),

  triggerSync: () =>
    api.post('/api/gnb-nodes/trigger-sync'),

  create: (data: Partial<GnbNode>) => api.post<GnbNode>('/api/gnb-nodes', data),

  update: (id: string, data: Partial<GnbNode>) => api.put<GnbNode>(`/api/gnb-nodes/${id}`, data),

  delete: (id: string) => api.delete(`/api/gnb-nodes/${id}`),

  addDraft: (id: string, data: Partial<GnbNodeDraft>) =>
    api.post<GnbNodeDraft>(`/api/gnb-nodes/${id}/drafts`, data)
};
